import { useLingui } from "@lingui/react";
import {
  ApiDatafanPipeline,
  FailureApi,
  IApiDatafanPipelineType,
} from "@smp/api";
import { Await, PartialRecord } from "@smp/utils";
import { useQuery, UseQueryOptions } from "react-query";

import { useQueryDatafanProject } from "@/queries/datafan/use_query_datafan_project";

const KEY = "datafan.pipeline.types";

export interface IDatafanPipelineType extends IApiDatafanPipelineType {
  is_depreciated: boolean;
}

type Response = Await<ReturnType<typeof ApiDatafanPipeline.Type.getList>>;
type ResponseSelect = PartialRecord<
  IApiDatafanPipelineType["id"],
  IDatafanPipelineType
>;

export const useQueryDatafanPipelineTypes = (
  options?: Omit<
    UseQueryOptions<Response, FailureApi, ResponseSelect>,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  const { i18n } = useLingui();
  const lang = i18n.locale === "ru" ? "ru" : "en";
  const {
    data: project,
    error: projectError,
    isLoading: projectIsLoading,
    isFetching: projectIsFetching,
  } = useQueryDatafanProject({
    enabled: options?.enabled,
  });

  const { data, error, isLoading } = useQuery<
    Response,
    FailureApi,
    ResponseSelect
  >(
    [KEY],
    () =>
      ApiDatafanPipeline.Type.getList({
        query: { lang },
      }),
    {
      enabled: !!project && options?.enabled,
      select: (data) =>
        Object.values(data).reduce<ResponseSelect>((acc, pipelineType) => {
          acc[pipelineType.id] = {
            ...pipelineType,
            is_depreciated:
              !project?.special_config.enable_pipeline_source.includes(
                pipelineType.id,
              ),
          };

          return acc;
        }, {}),
    },
  );

  return {
    data,
    error: error || projectError,
    isFetching: isLoading || projectIsFetching,
    isLoading: isLoading || projectIsLoading,
  };
};

useQueryDatafanPipelineTypes.KEY = KEY;
